import { useState, useEffect, createContext, useContext, useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { useDispatch, useSelector } from 'react-redux'
import { updateBien, createBien, emptyBien } from './biensSlice'
import { Input, Spinner, Toggle, Textarea } from '@/components/ui'
import useFormConfirmation from '@/hooks/useFormConfirmation'

export const getFormField = (control, {name, label, type, hint = "", className = ""}, index) => {
  switch(type) {
    case "currency":
      return <Input.Currency key={index}
        name={name}
        control={control}
        label={label}
      />
    case "date":
      return <Input.Date key={index}
        name={name}
        control={control}
        label={label}
        className={className}
      />
    case "toggle":
      return <Toggle.Form key={index}
        name={name}
        control={control}
        label={label}
        className={className.length > 0 ? className : 'w-full ms-3 ps-3'}
      />
    case "textarea":
      return <Textarea.Form 
        key={index}
        control={control} 
        name={name}
        label={label}
        hint={hint}
      />
    default:
      return <Input.Text key={index}
      name={name}
      control={control}
      label={label}
    />
  }
}

/**
 * @fileoverview Composant SectionForm - Formulaire intelligent pour la gestion des biens immobiliers
 *
 * Ce fichier contient le composant SectionForm qui sert de base réutilisable pour tous les formulaires
 * de gestion des biens immobiliers. Il encapsule la logique commune des formulaires (validation, soumission,
 * synchronisation avec Redux, gestion des erreurs) tout en permettant une personnalisation flexible
 * du rendu et des comportements spécifiques.
 *
 * @features
 * - Synchronisation bidirectionnelle avec le store Redux
 * - Gestion optimisée des objets complexes et tableaux pour éviter les rendus inutiles
 * - Validation des champs via react-hook-form
 * - Affichage unifié des erreurs (validation côté client et erreurs API)
 * - API contextuelle pour accéder aux méthodes du formulaire depuis les composants enfants
 * - Support pour les structures de données imbriquées et les relations complexes
 */

// Création d'un contexte pour partager les méthodes du formulaire
const FormContext = createContext(null)

/**
 * Hook personnalisé pour accéder au contexte du formulaire
 *
 * Permet aux composants enfants d'accéder aux méthodes et données du formulaire
 * sans avoir à les passer explicitement via les props.
 *
 * Le hook useFormContext donne accès aux éléments suivants :
 * 
 *  Propriété	      Type	    Description
 *  control	        Object	  Objet de contrôle react-hook-form pour les composants Controller
 *  watch	          Function	Fonction pour observer les valeurs du formulaire en temps réel
 *  setValue	      Function	Fonction pour définir les valeurs du formulaire
 *  formErrors	    Object	  Erreurs de validation côté client
 *  errors	        Object	  Erreurs renvoyées par l'API
 *  hasError	      Function	Fonction pour vérifier si un champ a une erreur
 *  getErrorMessage	Function	Fonction pour obtenir le message d'erreur d'un champ
 *  isLoading	      Boolean	Indique si le formulaire est en cours de chargement/soumission
 *  typeBien	      String	Type de bien sélectionné (pratique pour l'affichage conditionnel)
 *
 * @returns {Object} Les méthodes et données du formulaire
 * @throws {Error} Si utilisé en dehors d'un SectionForm
 */
export const useFormContext = () => {
  const context = useContext(FormContext)
  if (!context) {
    throw new Error(`useFormContext doit être utilisé à l'intérieur d'un SectionForm`)
  }
  return context
}

/**
 * Configuration des champs spéciaux du formulaire qui nécessitent une gestion particulière
 *
 * Chaque entrée définit:
 * - type: 'object' pour les objets simples, 'array' pour les tableaux d'objets
 * - compareKey: la clé à utiliser pour comparer les objets (généralement 'id')
 *
 * Cette configuration permet d'optimiser les rendus en évitant de mettre à jour
 * des champs complexes qui n'ont pas réellement changé.
 */
const specialFields = {
  // 'immeuble': { type: 'object', compareKey: 'id' },  // Objet simple, comparer par ID
  // 'proprietaires': { type: 'array', compareKey: 'id' }  // Tableau d'objets, comparer par ID
  // Ajouter d'autres champs spéciaux au besoin
}

/**
 * Composant SectionForm - Formulaire intelligent pour la gestion des biens immobiliers
 *
 * Ce composant encapsule la logique commune des formulaires (validation, soumission,
 * synchronisation avec Redux, gestion des erreurs) tout en permettant une personnalisation
 * flexible du rendu et des comportements spécifiques.
 *
 * @component SectionForm
 *
 * @param {Object} props Les propriétés du composant
 * @param {React.ReactNode|Function} props.children Le contenu du formulaire, peut être un noeud React ou une fonction de rendu
 * @param {string} props.title Le titre de la section du formulaire
 * @param {Function} [props.onClose=()=>{}] Fonction appelée lors de la fermeture/annulation du formulaire
 * @param {Function} [props.onSuccess=()=>{}] Fonction appelée après une soumission réussie
 * @param {string[]} [props.fields] Liste des champs à inclure dans ce formulaire (si défini, seuls ces champs seront synchronisés)
 * @param {string[]} [props.excludeFields] Liste des champs à exclure de ce formulaire
 * @returns {React.ReactElement} Le formulaire rendu
 */
const SectionForm = ({
  children,
  title,
  columns = 3,
  onClose = () => {},
  onSuccess = () => {},
  fields = null, // Liste des champs à inclure (prioritaire),
  addToSubmitFields = [], // Liste des champs manquants à ajouter au submit
  excludeFields = [] // Liste des champs à exclure
}) => {
  const dispatch = useDispatch()
  const bien = useSelector((state) => state.biens.inEdition || state.biens.current || emptyBien)
  const errors = useSelector((state) => state.biens.requestStatus.errors)
  const isCreating = useSelector((state) => state.biens.loadingStatus.create)
  const isUpdating = useSelector((state) => state.biens.loadingStatus.update)
  const isLoading = isCreating || isUpdating
  const [isSubmitting, setIsSubmitting] = useState(false)

  const formMethods = useForm({
    defaultValues: bien,
    mode: 'onChange',
    reValidateMode: 'onChange'
  })

  // Déterminer les champs à synchroniser
  const fieldsToSyncMemo = useMemo(
    () => fields
      ? fields // Si une liste spécifique est fournie, l'utiliser
      : Object.keys(bien).filter(key => !excludeFields.includes(key)) // Sinon utiliser tous les champs sauf les exclus,
    [fields, bien, excludeFields]
  )
  
  const { handleSubmit, watch,
    setValue, getValues,
    control, formState,
    reset
  } = formMethods
  const formErrors = formState.errors


  const onReset = () => {
    reset(bien)
  }

  const {
    handleFormCancel,
    setInitialLoadComplete,
    setInitialValues,
    setFormIsDirty,
    FormConfirmDialog
  } = useFormConfirmation({
    fieldsToWatch: fieldsToSyncMemo,
    watch,
    getValues,
    onReset,
    onClose
  })

  // Synchroniser le formulaire lorsque bien change - optimisé pour réduire les re-rendus
  useEffect(() => {
    if (!bien) return;
    reset(bien)

    // Synchroniser uniquement les champs requis
    fieldsToSyncMemo.forEach(key => {
      if (key in bien) {
        setValue(key, bien[key])
      }
    })
    
    // Marquer le chargement initial comme terminé et enregistrer les valeurs initiales
    // Utiliser un délai pour s'assurer que le formulaire est complètement mis à jour
    setTimeout(() => {
      setInitialValues(getValues())
      setInitialLoadComplete(true)
      setFormIsDirty(false) // Réinitialiser l'état dirty après le chargement initial
    }, 100)
  }, [bien])

  // Effet pour détecter les changements dans l'état de chargement
  useEffect(() => {
    if (isSubmitting && !isUpdating && !isCreating) {
      setIsSubmitting(false)
      if (!errors || Object.keys(errors).length === 0) {
        onSuccess()
      }
    }
  }, [isUpdating, isCreating, errors, isSubmitting, onSuccess, dispatch])

  /**
   * Récupère le message d'erreur pour un champ donné
   * Combine les erreurs de validation côté client et les erreurs API
   *
   * @param {string} fieldName - Nom du champ
   * @returns {string} Message d'erreur ou chaîne vide
   */
  const getErrorMessage = (fieldName) =>
    formErrors?.[fieldName]?.message || errors?.[fieldName] || ''

  /**
   * Détermine si un champ a une erreur
   *
   * @param {string} fieldName - Nom du champ
   * @returns {boolean} true si le champ a une erreur, false sinon
   */
  const hasError = (fieldName) =>
    !!formErrors?.[fieldName] || !!errors?.[fieldName]

  /**
   * Gère la soumission du formulaire
   * Dispatche l'action appropriée (create ou update) selon si le bien existe déjà
   * Préserve les champs qui ne sont pas gérés par ce formulaire spécifique
   *
   * @param {Object} formData - Données du formulaire
   */
  const onSubmit = async (formData) => {
    try {
      setIsSubmitting(true)

      // Si nous utilisons fields ou excludeFields, nous devons nous assurer
      // de ne pas écraser les champs qui ne font pas partie de ce formulaire
      let dataToSubmit = formData

      if (bien.id) {
        // Pour une mise à jour, fusionner les données soumises avec l'objet existant
        // mais uniquement pour les champs qui font partie de ce formulaire
        if (fields || excludeFields.length > 0) {
          const fieldsToInclude = fields || Object.keys(formData).filter(key => !excludeFields.includes(key))

          // Créer un nouvel objet avec uniquement les champs concernés
          dataToSubmit = {}
          fieldsToInclude.forEach(key => {
            if (key in formData) {
              dataToSubmit[key] = formData[key]
            }
          })

          if(addToSubmitFields.length > 0) {
            addToSubmitFields.forEach(key => {
              dataToSubmit[key] = formData[key]
            })
          }

        }

        await dispatch(updateBien({ id: bien.id, bienData: dataToSubmit }))
      } else {
        await dispatch(createBien(dataToSubmit))
      }
    } catch (error) {
      setIsSubmitting(false)
    }
  }

  // Valeurs à partager via le contexte
  const contextValue = {
    control,
    watch,
    setValue,
    getValues,
    formErrors,
    formState,
    errors,
    hasError,
    getErrorMessage,
    isLoading,
  }

  /**
   * Composant conteneur pour le formulaire
   * Permet une présentation cohérente et stylisée pour tous les formulaires
   *
   * @param {Object} props Les propriétés du composant
   * @param {React.ReactNode} props.children Le contenu du formulaire
   * @returns {React.ReactElement} Le conteneur rendu
   */
  const FormContainer = ({ children }) => (
    <div className="card bg-base-100 border border-base-200 rounded-none mb-4 max-h-[calc(80vh-150px)] overflow-y-auto">
      <div className="p-2 min-h-[400px]">
        <h3 className="text-xl mb-4">{title}</h3>
        <div className="card-body p-2">
          <div className={`grid grid-cols-1 md:grid-cols-${columns} gap-4`}>
            {children}
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <FormContext.Provider value={contextValue}>
      <div className="p-2">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-bold">
            {bien.id ? 'Modifier le bien' : 'Ajouter un bien'}
          </h2>
        </div>

        <form onSubmit={handleSubmit(onSubmit)}>
          {typeof children === 'function' 
            ? children({ FormContainer })
            : (
              <FormContainer>
                {children}
              </FormContainer>
            )
          }
          <div className="flex justify-between">
            <button
              type="button"
              className="btn btn-outline"
              onClick={handleFormCancel}
            >
              Annuler
            </button>
            <button
              type="submit"
              className={`btn ${isLoading ? 'loading' : ''} btn-primary text-white p-2 mt-4`}
              disabled={isLoading}
            >
              {isLoading ? <Spinner/> : 'Enregistrer'}
            </button>
          </div>
        </form>
        <FormConfirmDialog />
      </div>
    </FormContext.Provider>
  )
}

export default SectionForm
